import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSurveyCampaigns } from '@/features/surveys/api/use-survey-campaigns';
import type { SurveyCampaignListItem } from '@/features/surveys/types';

export const Route = createFileRoute('/_authenticated/surveys/campaigns/')({
  component: CampaignListPage,
});

function statusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'active':
      return 'default';
    case 'closed':
    case 'completed':
      return 'secondary';
    case 'draft':
      return 'outline';
    default:
      return 'outline';
  }
}

function CampaignListPage() {
  const { t } = useTranslation('surveys');
  const navigate = useNavigate();
  const { data, isLoading } = useSurveyCampaigns({});

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('campaigns.title')}</h1>
          <p className="text-muted-foreground">
            {t('campaigns.description')}
          </p>
        </div>
        <Link to="/surveys/campaigns/new">
          <Button data-testid="new-campaign-button">
            <Plus className="mr-2 h-4 w-4" />
            {t('campaigns.newCampaign')}
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={Send}
          title={t('campaigns.emptyTitle')}
          description={t('campaigns.emptyDescription')}
          action={{
            label: t('campaigns.newCampaign'),
            onClick: () => {
              void navigate({ to: '/surveys/campaigns/new' });
            },
          }}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('campaigns.name')}</TableHead>
                  <TableHead>{t('campaigns.template')}</TableHead>
                  <TableHead>{t('campaigns.status')}</TableHead>
                  <TableHead>{t('campaigns.recipients')}</TableHead>
                  <TableHead>{t('campaigns.submitted')}</TableHead>
                  <TableHead>{t('campaigns.sentAt')}</TableHead>
                  <TableHead>{t('campaigns.deadline')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((c: SurveyCampaignListItem) => (
                  <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <TableCell>
                      <Link
                        to="/surveys/campaigns/$campaignId"
                        params={{ campaignId: c.id }}
                        className="font-medium hover:underline"
                      >
                        {c.name}
                      </Link>
                    </TableCell>
                    <TableCell>{c.templateName}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(c.status)}>{c.status}</Badge>
                    </TableCell>
                    <TableCell>{c.recipientCount}</TableCell>
                    <TableCell>{c.submittedCount}</TableCell>
                    <TableCell>
                      {c.sentAt ? new Date(c.sentAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{new Date(c.deadline).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
